import { ALERTAS, type AlertaAtivo } from '@mymaxx/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell, BellOff, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { supervisaoApi } from '../lib/supervisao-api';

/** De quanto em quanto tempo o painel pergunta se algo saiu do lugar. */
const INTERVALO_MS = 30_000;

/** Quanto tempo a faixa do alerta novo fica no topo antes de virar só número no sino. */
const FAIXA_MS = 15_000;

/**
 * Alertas do líder, em qualquer tela do painel.
 *
 * Dois momentos diferentes, de propósito: o alerta **novo** aparece numa faixa no topo,
 * porque a pessoa pode estar em outra tela e precisa saber agora; depois de alguns
 * segundos a faixa sai sozinha e o alerta fica no sino, para ser conferido quando der.
 * Uma faixa que não sai vira parede, e parede a gente para de ler.
 *
 * Quem não criou nenhuma regra não recebe nada — e a consulta é barata: sem regra, o
 * servidor devolve lista vazia sem olhar conversa nenhuma.
 */
export function SinoAlertas() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [naFaixa, setNaFaixa] = useState<AlertaAtivo[]>([]);
  /** Ids já mostrados na faixa: alerta que continua ativo não pode reaparecer no topo. */
  const jaMostrados = useRef(new Set<string>());
  /** Temporizadores da faixa, cancelados só ao sair da tela (ver o efeito abaixo). */
  const relogios = useRef(new Set<ReturnType<typeof setTimeout>>());

  const painel = useQuery({
    queryKey: ['alertas'],
    queryFn: supervisaoApi.alertas,
    refetchInterval: INTERVALO_MS,
    // Erro aqui não é motivo para incomodar: alerta é acessório da tela, não o trabalho.
    retry: false,
  });

  const ativos = painel.data?.ativos ?? [];

  /**
   * O temporizador da faixa **não** pode ser cancelado na limpeza deste efeito.
   *
   * O efeito roda de novo a cada consulta (a lista chega como array novo de 30 em 30
   * segundos). Se a limpeza cancelasse o temporizador, a execução seguinte — que sai cedo
   * por não ter alerta novo — apagaria o prazo agendado pela anterior, e a faixa ficaria
   * para sempre na tela. Foi exatamente o que aconteceu ao testar: 16 segundos depois ela
   * continuava lá. Os temporizadores vivem num ref e só são cancelados ao sair da tela.
   */
  useEffect(() => {
    const novos = ativos.filter((a) => !jaMostrados.current.has(a.id));
    if (novos.length === 0) return;
    for (const a of novos) jaMostrados.current.add(a.id);
    setNaFaixa((atuais) => [...novos, ...atuais]);
    const id = setTimeout(() => {
      setNaFaixa((atuais) => atuais.filter((a) => !novos.some((n) => n.id === a.id)));
      relogios.current.delete(id);
    }, FAIXA_MS);
    relogios.current.add(id);
  }, [ativos]);

  useEffect(() => {
    const pendentes = relogios.current;
    return () => {
      for (const id of pendentes) clearTimeout(id);
      pendentes.clear();
    };
  }, []);

  async function silenciar(alerta: AlertaAtivo) {
    await supervisaoApi.silenciarAlerta(alerta.id, 30).catch(() => undefined);
    setNaFaixa((atuais) => atuais.filter((a) => a.id !== alerta.id));
    void qc.invalidateQueries({ queryKey: ['alertas'] });
  }

  function abrirAlerta(alerta: AlertaAtivo) {
    setAberto(false);
    setNaFaixa((atuais) => atuais.filter((a) => a.id !== alerta.id));
    // Alerta de conversa leva para a conversa; o resto leva ao Monitor, onde está o setor.
    navegar(alerta.conversaId ? `/atendimentos?conversa=${alerta.conversaId}` : '/monitor');
  }

  return (
    <>
      {naFaixa.length > 0 && (
        <div className="fixed inset-x-0 top-0 z-50 flex flex-col gap-px">
          {naFaixa.slice(0, 3).map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-3 bg-amber-500 px-4 py-2 text-sm text-amber-950 shadow"
            >
              <AlertTriangle size={16} className="shrink-0" />
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => abrirAlerta(a)}
              >
                <strong className="font-medium">{ALERTAS[a.tipo].rotulo}</strong> — {a.descricao}
              </button>
              <span className="shrink-0 text-xs opacity-80">{duracao(a.duracaoSeg)}</span>
              <button
                type="button"
                aria-label="Silenciar por 30 minutos"
                title="Já fui verificar — silenciar por 30 min"
                onClick={() => void silenciar(a)}
                className="shrink-0 rounded p-1 hover:bg-black/10"
              >
                <BellOff size={15} />
              </button>
              <button
                type="button"
                aria-label="Fechar aviso"
                onClick={() => setNaFaixa((atuais) => atuais.filter((x) => x.id !== a.id))}
                className="shrink-0 rounded p-1 hover:bg-black/10"
              >
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          aria-label={`Alertas${ativos.length ? ` (${ativos.length})` : ''}`}
          onClick={() => setAberto((a) => !a)}
          className="relative rounded-lg p-2 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
        >
          <Bell size={18} />
          {ativos.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-amber-500 px-1 text-[10px] leading-4 font-medium text-amber-950">
              {ativos.length}
            </span>
          )}
        </button>

        {aberto && (
          <div className="absolute right-0 z-40 mt-1 w-80 rounded-card border border-neutral-200 bg-white p-2 shadow-lg dark:border-neutral-800 dark:bg-neutral-900">
            {ativos.length === 0 ? (
              <p className="p-3 text-sm text-neutral-500">
                Nada fora do lugar agora.
                {painel.data?.silenciados ? ` ${painel.data.silenciados} silenciado(s).` : ''}
              </p>
            ) : (
              <ul className="max-h-80 space-y-1 overflow-y-auto">
                {ativos.map((a) => (
                  <li key={a.id}>
                    <div className="rounded-lg p-2 hover:bg-neutral-50 dark:hover:bg-neutral-800">
                      <button
                        type="button"
                        onClick={() => abrirAlerta(a)}
                        className="w-full text-left"
                      >
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                          {ALERTAS[a.tipo].rotulo} · {duracao(a.duracaoSeg)}
                        </p>
                        <p className="text-sm">{a.descricao}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => void silenciar(a)}
                        className="mt-1 text-xs text-neutral-500 underline"
                      >
                        Silenciar 30 min
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="border-t border-neutral-200 p-2 text-xs text-neutral-500 dark:border-neutral-800">
              As regras são suas e ficam no Monitor.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function duracao(segundos: number): string {
  if (segundos < 60) return 'agora';
  const min = Math.floor(segundos / 60);
  if (min < 60) return `há ${min} min`;
  const horas = Math.floor(min / 60);
  return `há ${horas}h${String(min % 60).padStart(2, '0')}`;
}
